import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, IconButton, Link, Collapse, alpha } from '@mui/material';
import { Cookie as CookieIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';

const ConsentBanner = ({ theme, onAccept, onDecline, onViewPreferences }) => {
  const [visible, setVisible] = useState(false);
  const [slideIn, setSlideIn] = useState(false);

  useEffect(() => {
    // Check if user has already given consent
    const consentStatus = localStorage.getItem('cookieConsent');

    if (!consentStatus) {
      // Show banner after a short delay for better UX
      setTimeout(() => {
        setVisible(true);
        setTimeout(() => setSlideIn(true), 100);
      }, 1000);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('consentTimestamp', new Date().toISOString());
    setSlideIn(false);
    setTimeout(() => {
      setVisible(false);
      if (onAccept) onAccept();
    }, 300);
  };

  const handleDeny = () => {
    localStorage.setItem('cookieConsent', 'denied');
    localStorage.setItem('consentTimestamp', new Date().toISOString());
    setSlideIn(false);
    setTimeout(() => {
      setVisible(false);
      if (onDecline) onDecline();
    }, 300);
  };

  const handleViewPreferences = () => {
    if (onViewPreferences) {
      onViewPreferences();
    } else {
      // Default behavior - could open a modal or navigate to preferences page
      console.log('View preferences clicked');
    }
  };

  if (!visible) return null;

  return (
    <Collapse in={slideIn} timeout={300}>
      <Paper
        elevation={12}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          background: `linear-gradient(135deg, ${theme?.dark || '#001845'} 0%, ${theme?.primary || '#0A2463'} 50%, ${theme?.secondary || '#3E92CC'} 100%)`,
          backdropFilter: 'blur(20px) saturate(180%)',
          borderTop: `2px solid ${alpha(theme?.accent || '#2EC4B6', 0.5)}`,
          boxShadow: `
            0 -8px 32px ${alpha(theme?.dark || '#001845', 0.4)},
            0 -4px 16px ${alpha(theme?.secondary || '#3E92CC', 0.2)},
            inset 0 1px 0 ${alpha('#fff', 0.1)}
          `,
          py: { xs: 2, md: 3 },
          px: { xs: 2, md: 4 },
        }}
      >
        <Box
          sx={{
            maxWidth: 1200,
            mx: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 2, md: 3 },
            flexWrap: { xs: 'wrap', md: 'nowrap' },
          }}
        >
          {/* Cookie Icon */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 50,
              height: 50,
              borderRadius: '50%',
              background: alpha('#fff', 0.15),
              backdropFilter: 'blur(10px)',
              flexShrink: 0,
            }}
          >
            <CookieIcon sx={{ fontSize: 26, color: theme?.accent || '#2EC4B6' }} />
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              minWidth: { xs: '100%', md: 0 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: '#fff',
                fontWeight: 600,
                mb: { xs: 0.75, md: 1 },
                fontSize: { xs: '1rem', md: '1.1rem' },
                letterSpacing: '0.01em',
              }}
            >
              We value your privacy
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: alpha('#fff', 0.9),
                fontSize: { xs: '0.875rem', md: '0.95rem' },
                lineHeight: 1.6,
                letterSpacing: '0.01em',
              }}
            >
              To provide the best experiences, we use technologies like cookies to store and/or
              access device information. Consenting to these technologies will allow us to process
              data such as browsing behaviour or unique IDs on this site. Not consenting or
              withdrawing consent, may adversely affect certain features and functions.
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 1, md: 1.5 },
              alignItems: 'center',
              minWidth: { xs: '100%', md: 'auto' },
              justifyContent: { xs: 'space-between', md: 'flex-start' },
              flexShrink: 0,
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
            }}
          >
            <Button
              variant="outlined"
              onClick={handleDeny}
              sx={{
                color: '#fff',
                borderColor: alpha('#fff', 0.4),
                px: { xs: 2, md: 2.5 },
                py: { xs: 0.75, md: 0.875 },
                fontWeight: 600,
                fontSize: { xs: '0.8125rem', md: '0.875rem' },
                textTransform: 'none',
                borderRadius: 2,
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease',
                minWidth: { xs: 85, md: 95 },
                '&:hover': {
                  borderColor: alpha('#fff', 0.6),
                  backgroundColor: alpha('#fff', 0.1),
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Deny
            </Button>
            <Button
              variant="outlined"
              onClick={handleViewPreferences}
              sx={{
                color: '#fff',
                borderColor: alpha(theme?.accent || '#2EC4B6', 0.5),
                px: { xs: 2, md: 2.5 },
                py: { xs: 0.75, md: 0.875 },
                fontWeight: 600,
                fontSize: { xs: '0.8125rem', md: '0.875rem' },
                textTransform: 'none',
                borderRadius: 2,
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease',
                minWidth: { xs: 130, md: 145 },
                '&:hover': {
                  borderColor: theme?.accent || '#2EC4B6',
                  backgroundColor: alpha(theme?.accent || '#2EC4B6', 0.1),
                  transform: 'translateY(-2px)',
                },
              }}
            >
              View Preferences
            </Button>
            <Button
              variant="contained"
              onClick={handleAccept}
              startIcon={<CheckCircleIcon />}
              sx={{
                background: `linear-gradient(135deg, ${theme?.accent || '#2EC4B6'} 0%, ${alpha(theme?.accent || '#2EC4B6', 0.8)} 100%)`,
                color: theme?.dark || '#001845',
                px: { xs: 2, md: 2.5 },
                py: { xs: 0.75, md: 0.875 },
                fontWeight: 700,
                fontSize: { xs: '0.8125rem', md: '0.875rem' },
                textTransform: 'none',
                borderRadius: 2,
                boxShadow: `0 4px 16px ${alpha(theme?.accent || '#2EC4B6', 0.3)}`,
                transition: 'all 0.3s ease',
                minWidth: { xs: 95, md: 105 },
                '&:hover': {
                  background: `linear-gradient(135deg, ${alpha(theme?.accent || '#2EC4B6', 0.9)} 0%, ${theme?.accent || '#2EC4B6'} 100%)`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 20px ${alpha(theme?.accent || '#2EC4B6', 0.4)}`,
                },
              }}
            >
              Accept
            </Button>
          </Box>
        </Box>
      </Paper>
    </Collapse>
  );
};

export default ConsentBanner;
